import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, useNotifications, usePrerenderGate } from '@byscott-io/corebyscott';
import { publicRecipes, recipes as recipesApi, recipeCategories } from '../../api/client';
import CategoryFilter from '../common/CategoryFilter';
import RecipeCount from './RecipeCount';

// Scope options for the toggle
const SCOPES = {
  ALL: 'all',
  KITCHEN: 'kitchen',
  FAVORITES: 'favorites',
};

export default function RecipeBrowser({
  showActions = false,
  defaultMyKitchen = false,
  detailPath = '/recipes', // '/recipes' for public, '/app/recipes' for app
  stickyTop = 'top-0', // Use 'top-16' when there's a header above
  onAddRecipe,
  onImportClick,
}) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const notifications = useNotifications();

  const [recipes, setRecipes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [scope, setScope] = useState(defaultMyKitchen ? SCOPES.KITCHEN : SCOPES.ALL);
  const [isScrolled, setIsScrolled] = useState(false);

  // Holds the prerender open until the listing has its recipes. Without this
  // a crawler could be served the heading and an empty grid: the renderer used
  // to call the page ready during the quiet gap before this fetch went out.
  usePrerenderGate(!loading, 'recipe-listing');

  const loadMoreRef = useRef(null);
  const isLoadingMore = useRef(false);
  const searchDebounceRef = useRef(null);

  // Determine which API to use based on scope
  const useAuthenticatedApi = scope !== SCOPES.ALL && isAuthenticated;

  // Scroll handler for sticky header collapse with hysteresis to prevent flicker
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setIsScrolled((prev) => {
        // Use different thresholds for collapsing vs expanding (hysteresis)
        if (prev) {
          // Currently collapsed - expand when scroll goes below 20
          return scrollY > 20;
        } else {
          // Currently expanded - collapse when scroll goes above 50
          return scrollY > 50;
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Load categories based on mode
  useEffect(() => {
    const loadCategories = async () => {
      try {
        let response;
        if (useAuthenticatedApi) {
          response = await recipeCategories.list();
        } else {
          response = await publicRecipes.categories();
        }
        setCategories(response.data.categories || []);
      } catch (err) {
        console.error('Failed to load categories:', err);
      }
    };
    loadCategories();
  }, [useAuthenticatedApi]);

  // Execute query with debounce
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      executeQuery();
    }, 300);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [search, selectedCategories, scope]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const currentRef = loadMoreRef.current;
    if (!currentRef) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore.current && !loading) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    observer.observe(currentRef);
    return () => observer.disconnect();
  }, [hasMore, loading, currentPage, search, selectedCategories, scope]);

  const executeQuery = async () => {
    try {
      setLoading(true);
      const params = { page: 1, per_page: 24 };

      if (selectedCategories.length > 0) {
        params.category_ids = selectedCategories;
      }

      // Add scope for favorites filter
      if (scope === SCOPES.FAVORITES) {
        params.scope = 'favorites';
      }

      let response;
      if (search.trim()) {
        params.q = search;
        response = useAuthenticatedApi
          ? await recipesApi.search(params)
          : await publicRecipes.search(params);
      } else {
        response = useAuthenticatedApi
          ? await recipesApi.list(params)
          : await publicRecipes.list(params);
      }

      setRecipes(response.data.recipes || []);
      setHasMore(response.data.meta?.has_more || false);
      setCurrentPage(1);
      setTotalCount(response.data.meta?.total_count || 0);
    } catch (err) {
      console.error('Failed to load recipes:', err);
      notifications?.error?.('Failed to load recipes');
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (isLoadingMore.current || !hasMore) return;
    isLoadingMore.current = true;
    setLoadingMore(true);

    try {
      const nextPage = currentPage + 1;
      const params = { page: nextPage, per_page: 24 };

      if (selectedCategories.length > 0) {
        params.category_ids = selectedCategories;
      }

      // Add scope for favorites filter
      if (scope === SCOPES.FAVORITES) {
        params.scope = 'favorites';
      }

      let response;
      if (search.trim()) {
        params.q = search;
        response = useAuthenticatedApi
          ? await recipesApi.search(params)
          : await publicRecipes.search(params);
      } else {
        response = useAuthenticatedApi
          ? await recipesApi.list(params)
          : await publicRecipes.list(params);
      }

      setRecipes((prev) => [...prev, ...(response.data.recipes || [])]);
      setHasMore(response.data.meta?.has_more || false);
      setCurrentPage(nextPage);
    } catch (err) {
      console.error('Failed to load more recipes:', err);
    } finally {
      setLoadingMore(false);
      isLoadingMore.current = false;
    }
  };

  const handleFeelingLucky = async () => {
    try {
      const params = {};
      if (search.trim()) params.q = search;
      if (selectedCategories.length > 0) params.category_ids = selectedCategories;

      const response = useAuthenticatedApi
        ? await recipesApi.feelingLucky(params)
        : await publicRecipes.feelingLucky(params);

      if (response.data.recipe) {
        const path = useAuthenticatedApi ? '/app/recipes' : '/recipes';
        navigate(`${path}/${response.data.recipe.id}`);
      } else {
        alert(response.data.message || 'No recipes found matching your filters');
      }
    } catch (err) {
      console.error('Feeling lucky failed:', err);
    }
  };

  const handleRecipeClick = (recipeId) => {
    const path = useAuthenticatedApi ? '/app/recipes' : detailPath;
    navigate(`${path}/${recipeId}`);
  };

  const handleSignupClick = useCallback(() => {
    navigate('/register');
  }, [navigate]);

  // Handle toggling favorite on a recipe
  const handleToggleFavorite = useCallback(async (recipeId, currentlyFavorited) => {
    try {
      const response = currentlyFavorited
        ? await recipesApi.unfavorite(recipeId)
        : await recipesApi.favorite(recipeId);

      if (response.data.success) {
        // Update the recipe in the local state
        setRecipes((prev) =>
          prev.map((r) =>
            r.id === recipeId
              ? { ...r, favorites_count: response.data.favorites_count, is_favorited: response.data.is_favorited }
              : r
          )
        );
        notifications?.success?.(currentlyFavorited ? 'Removed from favorites' : 'Added to favorites');
      }
    } catch (err) {
      console.error('Failed to toggle favorite:', err);
      notifications?.error?.('Failed to update favorites');
    }
  }, [isAuthenticated, navigate, notifications]);

  // Cycle through scopes: all -> kitchen -> favorites -> all
  const cycleScope = () => {
    if (scope === SCOPES.ALL) {
      setScope(SCOPES.KITCHEN);
    } else if (scope === SCOPES.KITCHEN) {
      setScope(SCOPES.FAVORITES);
    } else {
      setScope(SCOPES.ALL);
    }
  };

  const getScopeLabel = () => {
    switch (scope) {
      case SCOPES.KITCHEN:
        return '🏠 My Kitchen';
      case SCOPES.FAVORITES:
        return '❤️ Favorites';
      default:
        return '🌐 All Recipes';
    }
  };

  const getScopeStyle = () => {
    switch (scope) {
      case SCOPES.KITCHEN:
        return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300';
      case SCOPES.FAVORITES:
        return 'bg-pink-100 text-pink-700 dark:bg-pink-900/50 dark:text-pink-300';
      default:
        return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400';
    }
  };

  const handleReset = () => {
    setSearch('');
    setSelectedCategories([]);
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this recipe?')) return;
    try {
      await recipesApi.delete(id);
      notifications?.success?.('Recipe deleted');
      setRecipes((prev) => prev.filter((r) => r.id !== id));
      setTotalCount((prev) => prev - 1);
    } catch {
      notifications?.error?.('Failed to delete recipe');
    }
  };

  return (
    <div className="space-y-0">
      {/* Sticky Header */}
      <div className={`sticky ${stickyTop} z-30 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 transition-all duration-200 -mx-4 px-4 ${isScrolled ? 'py-2 shadow-sm' : 'py-4'}`}>
        {/* Title row - collapses when scrolled */}
        <div className={`transition-all duration-200 overflow-hidden ${isScrolled ? 'h-0 opacity-0 mb-0' : 'h-auto opacity-100 mb-4'}`}>
          {showActions ? (
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {scope === SCOPES.FAVORITES ? 'Favorite Recipes' : scope === SCOPES.KITCHEN ? 'My Recipes' : 'All Recipes'}
              </h1>
              <div className="flex gap-2">
                {onImportClick && (
                  <button
                    onClick={onImportClick}
                    className="inline-flex items-center px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    Import
                  </button>
                )}
                {onAddRecipe && (
                  <button
                    onClick={onAddRecipe}
                    className="inline-flex items-center px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    <span className="mr-1">+</span> Add Recipe
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center">
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                {scope === SCOPES.FAVORITES && isAuthenticated
                  ? 'Favorites'
                  : scope === SCOPES.KITCHEN && isAuthenticated
                    ? 'My Kitchen'
                    : 'Browse Recipes'}
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {scope === SCOPES.FAVORITES && isAuthenticated
                  ? 'Recipes you\'ve marked as favorites'
                  : scope === SCOPES.KITCHEN && isAuthenticated
                    ? 'Your private recipes (only you can see these)'
                    : 'Discover recipes shared by our community'}
              </p>
            </div>
          )}
        </div>

        {/* Search Row - always visible */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex-1 min-w-[200px] flex gap-2">
            <input
              type="text"
              placeholder="Search recipes..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
            {isAuthenticated && (
              <button
                onClick={cycleScope}
                className={`px-3 py-1.5 text-sm rounded-lg whitespace-nowrap transition-colors ${getScopeStyle()}`}
              >
                {getScopeLabel()}
              </button>
            )}
            <button
              onClick={handleFeelingLucky}
              className="px-3 py-1.5 text-sm bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors whitespace-nowrap"
              title="Suggest a random recipe"
            >
              🎲 Lucky
            </button>
          </div>
          {/* Compact action buttons when scrolled */}
          {isScrolled && showActions && (
            <div className="flex gap-2">
              {onImportClick && (
                <button
                  onClick={onImportClick}
                  className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Import
                </button>
              )}
              {onAddRecipe && (
                <button
                  onClick={onAddRecipe}
                  className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  + Add
                </button>
              )}
            </div>
          )}
        </div>

        {/* Category Filter */}
        {categories.length > 0 && (
          <div className="mt-2">
            <CategoryFilter
              categories={categories}
              selected={selectedCategories}
              onChange={setSelectedCategories}
              onReset={handleReset}
            />
          </div>
        )}

        {/* Recipe count */}
        {!loading && totalCount > 0 && (
          <RecipeCount
            total={totalCount}
            categories={categories}
            selectedCategoryIds={selectedCategories}
            searchTerm={search}
          />
        )}
      </div>

      {/* Recipe List */}
      <div className="pt-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : recipes.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {recipes.map((recipe) => (
                <RecipeCard
                  key={recipe.id}
                  recipe={recipe}
                  onClick={() => handleRecipeClick(recipe.id)}
                  href={useAuthenticatedApi ? null : `${detailPath}/${recipe.id}`}
                  onDelete={showActions && scope === SCOPES.KITCHEN ? () => handleDelete(recipe.id) : null}
                  onToggleFavorite={handleToggleFavorite}
                  isAuthenticated={isAuthenticated}
                  onSignupClick={handleSignupClick}
                />
              ))}
            </div>

            {/* Load more trigger */}
            <div ref={loadMoreRef} className="h-20 flex items-center justify-center mt-6">
              {loadingMore && (
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
              )}
              {!hasMore && recipes.length > 0 && (
                <p className="text-sm text-gray-400">You've seen all {totalCount} recipes</p>
              )}
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              {search || selectedCategories.length > 0
                ? 'No recipes found matching your filters.'
                : scope === SCOPES.FAVORITES
                  ? 'No favorites yet. Click the heart on any recipe to add it to your favorites.'
                  : scope === SCOPES.KITCHEN
                    ? 'No recipes yet.'
                    : 'No public recipes available yet.'}
            </p>
            {showActions && scope === SCOPES.KITCHEN && onAddRecipe && (
              <button onClick={onAddRecipe} className="text-indigo-600 hover:underline">
                Add your first recipe
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function RecipeCard({ recipe, onClick, href, onDelete, onToggleFavorite, isAuthenticated, onSignupClick }) {
  const favoritesCount = recipe.favorites_count || 0;
  const isFavorited = recipe.is_favorited || false;

  const handleHeartClick = (e) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      onSignupClick?.();
      return;
    }
    if (onToggleFavorite) {
      onToggleFavorite(recipe.id, isFavorited);
    }
  };

  const getHeartTitle = () => {
    if (!isAuthenticated) return 'Sign up to mark your favorites';
    return isFavorited ? 'Remove from favorites' : 'Add to favorites';
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-md transition-shadow">
      <div className="p-5 cursor-pointer" onClick={onClick}>
        <div className="flex justify-between items-start mb-2">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white flex-1 pr-2">
            {/* A REAL anchor, not just the card's onClick.
                Every public recipe is in sitemap.xml now, but a sitemap only
                gets a url discovered -- a crawler still needs a link to
                follow, and it cannot follow a click handler. This grid was
                the only route to a recipe page and offered none, so the
                pages were unreachable for crawlers however many were listed.
                Link also keeps client-side navigation for people, and
                stopPropagation avoids the card's own handler navigating a
                second time. */}
            {href ? (
              <Link to={href} onClick={(e) => e.stopPropagation()} className="hover:underline">
                {recipe.title}
              </Link>
            ) : (
              recipe.title
            )}
          </h3>
          {/* Heart icon with favorites count */}
          {/* Filled = you favorited, Outline pink = others favorited, Outline gray = no favorites */}
          <button
            onClick={handleHeartClick}
            className={`flex items-center gap-1 text-sm transition-colors ${
              !isAuthenticated
                ? 'text-gray-300 dark:text-gray-600 hover:text-pink-400'
                : isFavorited
                  ? 'text-pink-500 hover:text-pink-600'
                  : favoritesCount > 0
                    ? 'text-pink-400 hover:text-pink-500'
                    : 'text-gray-300 dark:text-gray-600 hover:text-pink-400'
            }`}
            title={getHeartTitle()}
          >
            {isFavorited ? (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            )}
            {favoritesCount > 0 && <span className="text-xs">{favoritesCount}</span>}
          </button>
        </div>
        {recipe.description && (
          <p className="text-gray-500 dark:text-gray-400 text-sm mb-3 line-clamp-2">
            {recipe.description}
          </p>
        )}
        {recipe.categories?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {recipe.categories.slice(0, 3).map((cat) => (
              <span
                key={cat.id}
                className="px-2 py-0.5 text-xs rounded-full"
                style={{ backgroundColor: cat.color + '20', color: cat.color }}
              >
                {cat.name}
              </span>
            ))}
          </div>
        )}
        <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 gap-3">
          {recipe.total_time_minutes > 0 && <span>⏱️ {recipe.total_time_minutes} min</span>}
          {recipe.servings && <span>🍽️ {recipe.servings}</span>}
        </div>
        {recipe.days_since_last_made !== null && recipe.days_since_last_made !== undefined && (
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
            Last made {recipe.days_since_last_made === 0 ? 'today' : `${recipe.days_since_last_made} days ago`}
          </p>
        )}
      </div>
      {onDelete && (
        <div className="px-5 pb-4 flex justify-end">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="text-red-600 hover:text-red-700 text-xs"
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
}
